import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '../types';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

type UserProfile = {
  id: string;
  email: string;
  full_name: string;
  farm_name: string;
  created_at: string;
  updated_at: string;
};

type AuthContextType = {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<{ error: any }>;
  signUp: (email: string, password: string, name: string, farmName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: any }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to get user profile
export const getUserProfile = async (userId: string) => {
  // Use only the new API service
  const { data, error } = await api.auth.getUser();
  if (error) {
    console.error('Error fetching user:', error);
    return null;
  }
  return data as UserProfile;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Handle user session and profile (no Supabase events)
  const handleAuthStateChange = useCallback(async (session: Session | null) => {
    if (session?.user) {
      setUser(session.user);
      const userProfile = await getUserProfile(session.user.id);
      setProfile(userProfile);
    } else {
      setUser(null);
      setProfile(null);
      if (!window.location.pathname.includes('login') && !window.location.pathname.includes('register')) {
        navigate('/login', { replace: true });
      }
    }
    setLoading(false);
  }, [navigate]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        // Check for existing session token
        const token = localStorage.getItem('access_token');
        if (token) {
          // Verify token with backend
          const { data: sessionData, error: sessionError } = await api.auth.getSession();
          if (sessionError) {
            console.error('Session verification failed:', sessionError);
            localStorage.removeItem('access_token');
            setUser(null);
            setProfile(null);
            return;
          }
          
          if (sessionData?.user) {
            setUser(sessionData.user);
            const userProfile = await getUserProfile(sessionData.user.id);
            setProfile(userProfile);
          }
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();
  }, []);

  // Sign up a new user
  const signUp = async (email: string, password: string, name: string, farmName: string) => {
    try {
      setLoading(true);
      
      // Create the user using the new API
      const { data: authData, error: signUpError } = await api.auth.signUp(email, password, name, farmName);
      
      if (signUpError) throw signUpError;
      
      // With the new API, user profile creation should be handled by the backend on signUp, or you should call a dedicated endpoint if needed.
      // If your backend creates the profile automatically, you can skip this block entirely.
      // If you need to call a separate endpoint, do so here. Otherwise, do nothing.
      // After sign up, set user/profile state from the returned session or user data if available.
      if (authData && authData.user) {
        setUser(authData.user);
        setProfile(authData.user); // Or fetch profile if separate
      }
      
      return { error: null };
    } catch (error: any) {
      console.error('Sign up error:', error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Sign in a user
  const signIn = async (email: string, password: string, rememberMe: boolean) => {
    try {
      setLoading(true);
      
      // Use the new API for sign in
      const { data, error } = await api.auth.signIn(email, password);
      
      // If there's an error, return it directly without throwing
      if (error) {
        console.error('Sign in error from API:', error);
        return { error };
      }
      
      // Process successful login
      if (data && data.user) {
        setUser(data.user);
        setProfile(data.user); // Or fetch profile if separate
        
        // Store remember me preference if needed
        if (rememberMe) {
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('remember_me');
        }
        
        return { error: null };
      } else {
        // If we have data but no user object, something is wrong
        console.error('Sign in returned data but no user object:', data);
        return { error: { message: 'Invalid response from authentication service' } };
      }
    } catch (error: any) {
      // This should only happen for unexpected errors
      console.error('Unexpected exception during sign in:', error);
      return { error: { message: error?.message || 'An unexpected error occurred' } };
    } finally {
      setLoading(false);
    }
  };

  // Sign out the current user
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await api.auth.signOut();
      
      // Always clear user state regardless of API response
      setUser(null);
      setProfile(null);
      
      // Log any errors but don't throw them
      if (error) {
        console.error('Error from signOut API:', error);
      }
      
      // Always navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Exception during sign out:', error);
      // Still clear user state and navigate to login
      setUser(null);
      setProfile(null);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Refresh the current session
  const refreshSession = async (): Promise<void> => {
    try {
      setLoading(true);
      // Use the new API to refresh session if available, otherwise re-fetch session
      const user = await api.auth.getUser();
      if (user) {
        setUser(user);
        setProfile(user); // Or fetch profile if separate
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      await signOut();
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: new Error('No user logged in') };
    
    try {
      setLoading(true);
      
      // Use the new API service for updating user profile
      // TODO: Implement user profile update endpoint in backend and expose via api.auth.updateProfile
      // @ts-expect-error: updateProfile does not exist yet, this is a placeholder for future implementation
      const { error } = await api.auth.updateProfile(updates);
      if (error) throw error;
        
      // Refresh profile
      const updatedProfile = await getUserProfile(user.id);
      setProfile(updatedProfile);
      
      return { error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        isAuthenticated: !!user,
        signIn,
        signUp,
        signOut,
        refreshSession,
        updateProfile
      }}
    >
      {!loading ? children : (
        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'background.default'
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
